import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { fieldErrorsOf } from '@/api/client';
import { useInvitationMutations, useMembers, usePendingInvitations } from '@/api/hooks';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Segmented } from '@/features/access/bits';
import { ROLE_OPTIONS, deliveryToast, validateInvite, type FieldErrors, type InviteValues } from './membersModel';

const EMPTY: InviteValues = { email: '', name: '', role: 'member', message: '' };

/** Invite by email with an org role; the person gets one email (sign-in details if new, else a link valid 7 days). */
export function InviteDialog({ disabled }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="members-invite-open" disabled={disabled} className="max-sm:h-11">
          <Icon name="person_add" size={18} />
          Invite people
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="members-invite-dialog" className="sm:max-w-lg" aria-describedby="members-invite-desc">
        {open ? <InviteForm onDone={() => setOpen(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const [values, setValues] = useState<InviteValues>(EMPTY);
  const [touched, setTouched] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const members = useMembers();
  const pending = usePendingInvitations();
  const { invite } = useInvitationMutations();
  const local: FieldErrors<'email' | 'message'> = validateInvite(values, members.data, pending.data);
  const errors = touched ? { ...serverErrors, ...local } : serverErrors;
  const set = (patch: Partial<InviteValues>) => { setValues((v) => ({ ...v, ...patch })); setServerErrors({}); };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(local).length) return;
    try {
      const body = { email: values.email.trim().toLowerCase(), orgRole: values.role, ...(values.name.trim() ? { name: values.name.trim() } : {}), ...(values.message.trim() ? { message: values.message.trim() } : {}) };
      const created = await invite.mutateAsync({ body, idempotencyKey });
      const sent = deliveryToast(created);
      if (sent.kind === 'warning') toast.warning(sent.title, { description: 'Use Resend in the pending list.' });
      else toast.success(sent.title, { description: sent.description ?? 'The link works once and expires in 7 days.' });
      onDone();
    } catch (err) {
      setServerErrors(fieldErrorsOf(err));
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} noValidate className="flex min-h-0 flex-col">
      <DialogHeader>
        <DialogTitle>Invite people</DialogTitle>
        <DialogDescription id="members-invite-desc">They get one email: their sign-in details if they are new to Hangar, a link to join if they already sign in. Signing in with that address makes them a member of this organization.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <FormField id="invite-email" label="Email" required error={errors.email} hint="Work email; they sign in with this address">
          <Input data-testid="invite-email" type="email" autoComplete="off" autoFocus value={values.email} onChange={(e) => set({ email: e.target.value })} onBlur={() => values.email && setTouched(true)} placeholder="name@company.com" {...fieldA11y('invite-email', errors.email, 'hint')} />
        </FormField>
        <FormField id="invite-name" label="Name" hint="Optional: used in the email">
          <Input data-testid="invite-name" value={values.name} onChange={(e) => set({ name: e.target.value })} maxLength={120} {...fieldA11y('invite-name', undefined, 'hint')} />
        </FormField>
        <div className="flex flex-col gap-1.5">
          <Segmented label="Role" testId="invite-role" value={values.role} onChange={(role) => set({ role })} options={ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.description }))} />
          <p className="text-xs text-subtle">{ROLE_OPTIONS.find((o) => o.value === values.role)?.description}</p>
          {errors.orgRole ? <p className="text-xs text-destructive">{errors.orgRole}</p> : null}
        </div>
        <FormField id="invite-message" label="Personal note" error={errors.message} hint="Optional: added to the email">
          <Textarea data-testid="invite-message" rows={3} value={values.message} onChange={(e) => set({ message: e.target.value })} {...fieldA11y('invite-message', errors.message, 'hint')} />
        </FormField>
        {invite.error && !Object.keys(serverErrors).length ? <ProblemAlert error={invite.error} testId="invite-error" /> : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} className="max-sm:h-11">Cancel</Button>
        <Button type="submit" data-testid="invite-submit" disabled={invite.isPending} className="max-sm:h-11">
          {invite.isPending ? 'Sending…' : 'Send invitation'}
        </Button>
      </DialogFooter>
    </form>
  );
}
